import {
  FormEvent,
  FunctionComponent,
  ReactNode,
  useState,
  useRef,
  Fragment,
  useCallback,
  ChangeEvent,
} from 'react'
import { useHistory } from 'react-router'
import { Input, Button, Link, toast } from '@conexasaude/hero'

import Icon from '@/components/HeroIcons'

import services from '../../auth-services'
import { useStyles } from './useStyles'
import ReCAPTCHA from 'react-google-recaptcha'
import { useChatLayerStore } from '@/components/ChatLayer'

interface ConfirmPasswordProps {
  onConfirm: () => void
}

const ConfirmPassword: FunctionComponent<ConfirmPasswordProps> = ({
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [password, setPassword] = useState<string>()
  const [shouldShowPassword, setShouldShowPassword] = useState<boolean>(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const { push } = useHistory()

  const { container, inputIcon, LinkSupport } = useStyles()
  const openChatLayer = useChatLayerStore(state => state.openChatLayer)

  const hidePassword = useCallback(() => {
    setShouldShowPassword(false)
  }, [])

  const showPassword = useCallback(() => {
    setShouldShowPassword(true)
  }, [])

  const choosePasswordIcon = (): ReactNode => {
    return shouldShowPassword ? (
      <Icon
        name="eye-off"
        themeColor={['extra', 'gray_icon']}
        onClick={hidePassword}
      />
    ) : (
      <Icon
        name="eye-on"
        themeColor={['extra', 'gray_icon']}
        onClick={showPassword}
      />
    )
  }

  const handleVerifyPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    try {
      const token = await recaptchaRef.current.executeAsync()
      const verified = await services.verifyPassword(password, token)

      if (verified) {
        onConfirm()
      } else {
        toast('Senha incorreta', {
          variant: 'danger',
          autoClose: 5000,
        })
        recaptchaRef.current.reset()
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.msg || err?.response?.msg || err.message

      toast(errorMessage, {
        variant: 'danger',
        autoClose: 5000,
      })
      recaptchaRef.current.reset()
    }
    setIsLoading(false)
  }

  const handlePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value)
    },
    [],
  )

  const goToForgotPassword = useCallback(() => {
    push('/esqueci-senha')
  }, [push])

  return (
    <Fragment>
      <form css={container} onSubmit={handleVerifyPassword}>
        <h2>Qual é a sua senha atual?</h2>
        <p>
          Para criar uma nova senha, nos informe a senha que você utiliza
          atualmente.
        </p>

        <Input
          label="Senha atual"
          placeholder="Informe a senha atual"
          inputProps={{ type: shouldShowPassword ? 'text' : 'password' }}
          onChange={handlePasswordChange}
          required
          icon={choosePasswordIcon()}
          styles={{ icon: inputIcon }}
          fullWidth
        />

        <Button size="md" busy={isLoading} disabled={isLoading} fullWidth>
          Confirmar senha
        </Button>
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        />
        <Link onClick={goToForgotPassword}>Esqueci minha senha</Link>
      </form>

      <Link css={LinkSupport} onClick={openChatLayer}>
        Precisa de ajuda?
      </Link>
    </Fragment>
  )
}

export { ConfirmPassword }
